import { useMemo } from 'react'
import useCharterRewardAvailability from './useCharterRewardAvailability'

/**
 * Minimal flight item shape — works with both checkout FlightItem and multi-cart FlightItem
 * without importing the full type, so the hook is usable across cart, checkout, and upsell surfaces.
 */
interface RewardFlightItemLike {
  isClassicReward?: boolean
  passengers: Array<{ type: string }>
  flights: Array<{
    rewardJourneyKey?: string
    journeyKey: string
    cost?: number
  }>
}

export interface ClassicRewardCartPricing {
  /** True if any flight item is a classic reward booking. */
  isReward: boolean
  /** True while the availability API is still loading. */
  loading: boolean
  /** True when availability has returned and all reward flights are available. */
  available: boolean
  /** Total points across all reward flights and all seat-eligible passengers. */
  totalPoints: number
  /** Total flight cash co-pay across all reward flights and all seat-eligible passengers. */
  totalCashCoPay: number
  /** Per-person points across all reward flight legs (before passenger multiplication). */
  pointsPerPerson: number | undefined
  /** Per-person cash co-pay across all reward flight legs (before passenger multiplication). */
  cashCoPayPerPerson: number | undefined
  /** Number of seat-eligible passengers (adults + children, excludes infants). */
  passengerCount: number
  /** Number of reward flights in the input. */
  rewardFlightCount: number
}

const EMPTY_RESULT: ClassicRewardCartPricing = {
  isReward: false,
  loading: false,
  available: false,
  totalPoints: 0,
  totalCashCoPay: 0,
  pointsPerPerson: undefined,
  cashCoPayPerPerson: undefined,
  passengerCount: 0,
  rewardFlightCount: 0,
}

/**
 * Shared hook that computes Classic Rewards pricing for a list of flight items.
 *
 * Finds all flights with `isClassicReward`, flattens their legs into a single
 * `useCharterRewardAvailability` call (React Query dedup ensures no duplicate
 * API calls if the same legs are queried elsewhere), and returns aggregated
 * points + cash totals.
 *
 * Use this hook in cart page, LuxPlus upsell, and any display surface that
 * needs reward pricing from flight items. For checkout payment selectors that
 * can't call hooks, the Redux `classicRewards` state is still needed.
 */
export default function useClassicRewardCartPricing(
  flightItems: ReadonlyArray<RewardFlightItemLike>,
  options?: { disabled?: boolean },
): ClassicRewardCartPricing {
  const disabled = options?.disabled ?? false

  const rewardFlights = useMemo(
    () => flightItems.filter(f => f.isClassicReward),
    [flightItems],
  )

  const passengerCount = useMemo(
    () => rewardFlights[0]?.passengers.filter(p => p.type !== 'infant').length ?? 0,
    [rewardFlights],
  )

  const legs = useMemo(
    () => rewardFlights.flatMap(flight =>
      flight.flights.map(f => ({
        journeyKey: f.rewardJourneyKey ?? f.journeyKey,
        legTotal: f.cost ?? 0,
      })),
    ),
    [rewardFlights],
  )

  const {
    available,
    isLoading,
    pointsPerPerson,
    cashCoPayPerPerson,
  } = useCharterRewardAvailability({
    legs,
    passengerCount,
    disabled: disabled || rewardFlights.length === 0 || legs.length === 0,
  })

  return useMemo<ClassicRewardCartPricing>(() => {
    if (rewardFlights.length === 0) return EMPTY_RESULT

    if (!available || pointsPerPerson == null || cashCoPayPerPerson == null) {
      return {
        ...EMPTY_RESULT,
        isReward: true,
        loading: isLoading || !available,
        passengerCount,
        rewardFlightCount: rewardFlights.length,
      }
    }

    return {
      isReward: true,
      loading: false,
      available: true,
      totalPoints: pointsPerPerson * passengerCount,
      totalCashCoPay: cashCoPayPerPerson * passengerCount,
      pointsPerPerson,
      cashCoPayPerPerson,
      passengerCount,
      rewardFlightCount: rewardFlights.length,
    }
  }, [rewardFlights.length, available, isLoading, pointsPerPerson, cashCoPayPerPerson, passengerCount])
}
